import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useOrganizations } from 'data/repositories/organizationsRepository';

const OrganizationContext = createContext(null);

// One extra attempt after a failed organization list fetch. Without it a
// single failed GET /api/organizations leaves the list empty for the whole
// session, with no path back to a real count.
const ORGANIZATION_LIST_RETRY_LIMIT = 1;

/**
 * Decide whether the multi-organization controls (the Organizations nav entry,
 * the Switch Organization toggle) are shown for the current list state.
 *
 * They are hidden only for a CONFIRMED single-organization user, which is what
 * the product owner asked for. A count read on its own cannot tell "exactly
 * one organization" from "the list never loaded", so a failed fetch would
 * latch those controls off for the rest of the session and present as missing
 * navigation. A list carrying a load error is therefore never treated as a
 * confirmed single-organization answer.
 */
export const shouldShowMultiOrganizationControls = ({ items, error } = {}) =>
	!!error || (items?.length || 0) > 1;

export const OrganizationProvider = ({ children }) => {
	const { items, active, isLoading, isLoadingActive, error, actions } =
		useOrganizations();
	const isLoggedIn = useSelector((state) => !!state.user.authToken);

	/**
	 * Fetch the organization list, retrying a bounded number of times so a
	 * transient failure does not leave the list permanently empty. The
	 * rejection is handled here rather than escaping as an unhandled promise,
	 * and every failed attempt is logged with its context.
	 */
	const fetchOrganizationList = useCallback(async () => {
		for (
			let attempt = 0;
			attempt <= ORGANIZATION_LIST_RETRY_LIMIT;
			attempt += 1
		) {
			try {
				await actions.fetchAll();
				return;
			} catch (listError) {
				console.error('OrganizationProvider: organization list fetch failed', {
					attempt,
					retryLimit: ORGANIZATION_LIST_RETRY_LIMIT,
					error: listError,
				});
			}
		}
	}, [actions]);

	/**
	 * Fetch the active organization. Its failure is independent of the list's
	 * and is logged rather than swallowed.
	 */
	const fetchActiveOrganization = useCallback(async () => {
		try {
			await actions.getActive();
		} catch (activeError) {
			console.error(
				'OrganizationProvider: active organization fetch failed',
				activeError,
			);
		}
	}, [actions]);

	// Load organizations and active on mount -- only when authenticated.
	// Organizations are a post-login feature; calling these without a token
	// hits the jwt.auth guard and 401s on the public/logged-out site.
	useEffect(() => {
		if (!isLoggedIn) return;
		fetchOrganizationList();
		fetchActiveOrganization();
	}, [fetchOrganizationList, fetchActiveOrganization, isLoggedIn]);

	// Sync active organization ID to localStorage
	useEffect(() => {
		if (active?.id) {
			localStorage.setItem('active_organization_id', active.id);
			sessionStorage.setItem('active_organization_id', active.id);
		}
	}, [active]);

	const contextValue = useMemo(
		() => ({
			organizations: items,
			activeOrganization: active,
			isLoading: isLoadingActive,
			// Load state of the organization LIST, so consumers can tell an
			// empty list from a list that never loaded.
			isLoadingOrganizations: isLoading,
			organizationsError: error,
			showsMultipleOrganizations: shouldShowMultiOrganizationControls({
				items,
				error,
			}),
			switchOrganization: (id) => actions.switchOrganization({ id }),
			refreshOrganizations: () => actions.fetchAll(),
			retryOrganizations: fetchOrganizationList,
		}),
		[
			items,
			active,
			isLoading,
			isLoadingActive,
			error,
			actions,
			fetchOrganizationList,
		],
	);

	return (
		<OrganizationContext.Provider value={contextValue}>
			{children}
		</OrganizationContext.Provider>
	);
};

// Custom hook to use organization context
export const useOrganizationContext = () => {
	const context = useContext(OrganizationContext);
	if (!context) {
		throw new Error(
			'useOrganizationContext must be used within OrganizationProvider',
		);
	}
	return context;
};
